import { BearerToken, APIError } from '../../api';
import HundredBricksAPI from '../HundredBricksAPI';
import Antiphishing from './Antiphishing';
import AntiphishingError from './AntiphishingError';
import AuthenticationAttemptsLimitReached from './AuthenticationAttemptsLimitReached';
import AuthenticationPingError from './AuthenticationPingError';
import ChangeSessionError from './ChangeSessionError';
import DistanceExceededFromLastConnection from './DistanceExceededFromLastConnection';
import EmailNotGivenByTheInvestor from './EmailNotGivenByTheInvestor';
import Geolocation from './Geolocation';
import InactiveAccount from './InactiveAccount';
import InvalidCredentials from './InvalidCredentials';
import InvalidLatitude from './InvalidLatitude';
import InvalidLongitude from './InvalidLongitude';
import InvalidToken from './InvalidToken';
import InvestorHasAlreadyAnActiveSession from './InvestorHasAlreadyAnActiveSession';
import InvestorIsNotLocked from './InvestorIsNotLocked';
import InvestorIsNotRegistered from './InvestorIsNotRegistered';
import LoginError from './LoginError';
import LogoutError from './LogoutError';
import ResendInactiveAccountError from './ResendInactiveAccountError';
import ResendUnusualAccessError from './ResendUnusualAccessError';
import SendInactiveAccountError from './SendInactiveAccountError';
import SendUnusualAccessError from './SendUnusualAccessError';
import Session from './Session';
import TraderIsNotBlocked from './TraderIsNotBlocked';
import UnusualAccess from './UnusualAccess';
class AuthenticationAPI extends HundredBricksAPI {
    async antiphishing(email) {
        try {
            const { data } = await this.client.get('v2/profile/antiphishing', { params: { email } });
            return new Antiphishing(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'MISSING_EMAIL':
                        throw new EmailNotGivenByTheInvestor({ cause: error });
                    case 'INVESTOR_NOT_FOUND':
                        throw new InvestorIsNotRegistered(email, { cause: error });
                    default:
                        throw new AntiphishingError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new AntiphishingError('Unexpected antiphishing error', { cause: error });
            }
            else {
                throw new AntiphishingError(`Unexpected antiphishing error: '${error}'`);
            }
        }
    }
    async login(email, password, latitude, longitude) {
        try {
            const { data } = await this.client.post('/v1/login', {
                email,
                password,
                latitude,
                longitude
            });
            const token = new BearerToken(data.token);
            const geolocation = new Geolocation(data.geolocalization);
            return new Session(token, geolocation);
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'INCORRECT_PASSWORD':
                        throw new InvalidCredentials({ cause: error });
                    case 'AUTHENTICATION_ATTEMPS_LIMIT_REACHED':
                        throw new AuthenticationAttemptsLimitReached({ cause: error });
                    case 'INVALID_USER_CONNECTION':
                        throw new DistanceExceededFromLastConnection({ cause: error });
                    case 'TRADER_ALREADY_HAS_ACTIVE_SESSION':
                        throw new InvestorHasAlreadyAnActiveSession({ cause: error });
                    case 'UNUSUAL_ACCESS':
                        throw new UnusualAccess({ cause: error });
                    case 'INACTIVE_ACCOUNT':
                        throw new InactiveAccount({ cause: error });
                    case 'UNAUTHORIZED':
                        throw new InvestorIsNotRegistered(email, { cause: error });
                    case 'INVALID_LATITUDE':
                        throw new InvalidLatitude({ cause: error });
                    case 'INVALID_LONGITUDE':
                        throw new InvalidLongitude({ cause: error });
                    default:
                        throw new LoginError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new LoginError('Unexpected login error', { cause: error });
            }
            else {
                throw new LoginError(`Unexpected login error: '${error}'`);
            }
        }
    }
    async logout() {
        try {
            await this.client.post('/v1/logout');
        }
        catch (error) {
            if (error instanceof APIError) {
                if (error.code === 'UNAUTHORIZED')
                    return;
                throw new LogoutError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new LogoutError('Unexpected logout error', { cause: error });
            }
            else {
                throw new LogoutError(`Unexpected logout error: '${error}'`);
            }
        }
    }
    async ping() {
        try {
            await this.client.get('/v1/traders/me');
        }
        catch (error) {
            if (error instanceof Error) {
                throw new AuthenticationPingError({ cause: error });
            }
            else {
                throw new AuthenticationPingError(`Unexpected ping error: '${error}'`);
            }
        }
    }
    /**
     * Add location to current session
     *
     * @param latitude
     * @param longitude
     *
     * @returns Session
     */
    async changeSession(latitude, longitude) {
        try {
            const { data } = await this.client.post('/v2/changeSession', { latitude, longitude });
            const token = new BearerToken(data.token);
            const geolocation = new Geolocation(data.geolocalization);
            return new Session(token, geolocation);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new ChangeSessionError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new ChangeSessionError('Unexpected change session error', { cause: error });
            }
            else {
                throw new ChangeSessionError(`Unexpected change session error: '${error}'`);
            }
        }
    }
    /**
     * Unblock investor when this is blocked 'cause unusual access
     *
     * @param code
     * @param traderId
     * @param password
     * @param latitude
     * @param longitude
     *
     * @returns Session
     */
    async unblockInvestor(params) {
        const { traderId } = params;
        try {
            const { data } = await this.client.post('/v1/unblockTrader', params);
            const token = new BearerToken(data.token);
            const geolocation = new Geolocation(data.geolocalization);
            return new Session(token, geolocation);
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'INCORRECT_PASSWORD':
                        throw new InvalidCredentials({ cause: error });
                    case 'TRADER_ALREADY_HAS_ACTIVE_SESSION':
                        throw new InvestorHasAlreadyAnActiveSession({ cause: error });
                    case 'UNAUTHORIZED':
                        throw new InvestorIsNotRegistered(traderId, { cause: error });
                    case 'INVALID_LATITUDE':
                        throw new InvalidLatitude({ cause: error });
                    case 'INVALID_LONGITUDE':
                        throw new InvalidLongitude({ cause: error });
                    default:
                        throw new LoginError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new LoginError('Unexpected unblock investor error', { cause: error });
            }
            else {
                // We are conserving the login error because unblock Investor returns a session.
                throw new LoginError(`Unexpected unblock investor error: '${error}'`);
            }
        }
    }
    /**
     * Unlocks an investor's account that was locked due to inactivity
     *
     * @param code
     * @param investorId
     * @param latitude
     * @param longitude
     * @param password
     *
     * @returns Session
     */
    async unlockInactiveAccount(params) {
        const { investorId } = params;
        try {
            const { data } = await this.client.post('/v2/traders/unlock/unlockInactiveAccount', params);
            const token = new BearerToken(data.token);
            const geolocation = new Geolocation(data.geolocalization);
            return new Session(token, geolocation);
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'INCORRECT_PASSWORD':
                        throw new InvalidCredentials({ cause: error });
                    case 'INVESTOR_ALREADY_HAS_ACTIVE_SESSION':
                        throw new InvestorHasAlreadyAnActiveSession({ cause: error });
                    case 'UNAUTHORIZED':
                        throw new InvestorIsNotRegistered(investorId, { cause: error });
                    case 'INVALID_USER_CONNECTION':
                        throw new DistanceExceededFromLastConnection({ cause: error });
                    case 'INVALID_TOKEN':
                        throw new InvalidToken({ cause: error });
                    case 'INVALID_LATITUDE':
                        throw new InvalidLatitude({ cause: error });
                    case 'INVALID_LONGITUDE':
                        throw new InvalidLongitude({ cause: error });
                    default:
                        throw new LoginError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new LoginError('Unexpected unlock investor error', { cause: error });
            }
            else {
                throw new LoginError(`Unexpected unlock investor error: '${error}'`);
            }
        }
    }
    /**
     * Sends the one-time token to begin the inactive account unlock process
     *
     * @param investorId
     * @param channel
     *
     * @returns empty object {}
     */
    async sendInactiveAccountToken(investorId, channel) {
        try {
            const { data } = await this.client.post('/v2/traders/unlock/sendInactiveAccountToken', { investorId, channel });
            return data;
        }
        catch (error) {
            if (error instanceof APIError) {
                if (error.code === 'INVESTOR_NOT_BLOCKED_BY_INACTIVE_ACCOUNT') {
                    throw new InvestorIsNotLocked(investorId, { cause: error });
                }
                else {
                    throw new SendInactiveAccountError({ cause: error });
                }
            }
            if (error instanceof Error) {
                throw new SendInactiveAccountError('Unexpected while sending inactive account code', { cause: error });
            }
            else {
                throw new SendInactiveAccountError(`Unexpected while sending inactive account code: '${error}'`);
            }
        }
    }
    /**
     * Send investors unlock code to unblock the investor's account
     *
     * @param traderId
     * @param channel
     *
     * @returns empty object {}
     */
    async sendUnusualCodeAccess(traderId, channel) {
        try {
            const { data } = await this.client.post('/v1/unblockTrader/sendUnblockToken', { traderId, channel });
            return data;
        }
        catch (error) {
            if (error instanceof APIError) {
                if (error.code === 'TRADER_NOT_BLOCKED') {
                    throw new TraderIsNotBlocked(traderId, { cause: error });
                }
                else {
                    throw new SendUnusualAccessError({ cause: error });
                }
            }
            if (error instanceof Error) {
                throw new SendUnusualAccessError('Unexpected while sending unusual access code', { cause: error });
            }
            else {
                throw new SendUnusualAccessError(`Unexpected while sending unusual access code: '${error}'`);
            }
        }
    }
    /**
     * Resend investors unlock code to unblock the investor's account
     *
     * @param email
     *
     * @returns empty object {}
     */
    async resendUnusualCodeAccess(email) {
        try {
            const { data } = await this.client.post('/v1/traders/resendUnusualAccessEmail', { email });
            return data;
        }
        catch (error) {
            if (error instanceof APIError) {
                if (error.code === 'TRADER_NOT_BLOCKED') {
                    throw new TraderIsNotBlocked(email, { cause: error });
                }
                throw new ResendUnusualAccessError('Unexpected while resending unusual access code', { cause: error });
            }
            else if (error instanceof Error) {
                throw new ResendUnusualAccessError('Unexpected while resending unusual access code', { cause: error });
            }
            else {
                throw new ResendUnusualAccessError(`Unexpected while resending unusual access code: '${error}'`);
            }
        }
    }
    async resendInactiveCodeAccess(email) {
        try {
            const { data } = await this.client.post('/v2/traders/unlock/resendInactiveAccountLock', { email });
            return data;
        }
        catch (error) {
            if (error instanceof APIError) {
                if (error.code === 'INVESTOR_NOT_BLOCKED') {
                    throw new InvestorIsNotLocked(email, { cause: error });
                }
                throw new ResendInactiveAccountError('Unexpected while resending inactive account code', { cause: error });
            }
            else if (error instanceof Error) {
                throw new ResendInactiveAccountError('Unexpected while resending inactive account code', { cause: error });
            }
            else {
                throw new ResendInactiveAccountError(`Unexpected while resending inactive account code: '${error}'`);
            }
        }
    }
}
export default AuthenticationAPI;
